import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

import API_BASE_URL from "../config/api";


function AddSubject() {

    const navigate = useNavigate();


    /*
    |--------------------------------------------------------------------------
    | Form Data
    |--------------------------------------------------------------------------
    */

    const [formData, setFormData] = useState({
        subject_name: "",
        status: "Enable",
        iconimage: null
    });


    /*
    |--------------------------------------------------------------------------
    | Image Preview
    |--------------------------------------------------------------------------
    */

    const [imagePreview, setImagePreview] = useState(null);


    /*
    |--------------------------------------------------------------------------
    | Loading State
    |--------------------------------------------------------------------------
    */

    const [saving, setSaving] = useState(false);


    /*
    |--------------------------------------------------------------------------
    | Handle Input Change
    |--------------------------------------------------------------------------
    */

    const handleChange = (e) => {

        const {
            name,
            value
        } = e.target;


        setFormData((previousData) => ({

            ...previousData,

            [name]: value

        }));

    };


    /*
    |--------------------------------------------------------------------------
    | Handle Icon Image Change
    |--------------------------------------------------------------------------
    */

    const handleImageChange = (e) => {

        const file = e.target.files?.[0];


        if (!file) {

            return;

        }


        /*
        |--------------------------------------------------------------------------
        | Allowed Image Types
        |--------------------------------------------------------------------------
        */

        const allowedTypes = [

            "image/jpeg",

            "image/jpg",

            "image/png",

            "image/webp",

            "image/gif"

        ];


        if (!allowedTypes.includes(file.type)) {

            Swal.fire({

                icon: "warning",

                title: "Invalid Image",

                text:
                    "Only JPG, JPEG, PNG, WEBP and GIF images are allowed."

            });


            e.target.value = "";

            return;

        }


        /*
        |--------------------------------------------------------------------------
        | Maximum 2 MB
        |--------------------------------------------------------------------------
        */

        if (file.size > 2 * 1024 * 1024) {

            Swal.fire({

                icon: "warning",

                title: "Image Too Large",

                text:
                    "Icon image size must be less than 2 MB."

            });


            e.target.value = "";

            return;

        }


        /*
        |--------------------------------------------------------------------------
        | Save Image in Form Data
        |--------------------------------------------------------------------------
        */

        setFormData((previousData) => ({

            ...previousData,

            iconimage: file

        }));


        /*
        |--------------------------------------------------------------------------
        | Image Preview
        |--------------------------------------------------------------------------
        */

        const previewUrl =
            URL.createObjectURL(file);


        setImagePreview(previewUrl);

    };


    /*
    |--------------------------------------------------------------------------
    | Submit Form
    |--------------------------------------------------------------------------
    */

    const handleSubmit = async (e) => {

        e.preventDefault();


        /*
        |--------------------------------------------------------------------------
        | Subject Name Validation
        |--------------------------------------------------------------------------
        */

        if (!formData.subject_name.trim()) {

            Swal.fire({

                icon: "warning",

                title: "Subject Name Required",

                text:
                    "Please enter subject name."

            });

            return;

        }


        /*
        |--------------------------------------------------------------------------
        | Icon Image Validation
        |--------------------------------------------------------------------------
        */

        if (!formData.iconimage) {

            Swal.fire({

                icon: "warning",

                title: "Icon Image Required",

                text:
                    "Please select subject icon image."

            });

            return;

        }


        try {

            setSaving(true);


            /*
            |--------------------------------------------------------------------------
            | FormData
            |--------------------------------------------------------------------------
            */

            const data = new FormData();


            data.append(
                "subject_name",
                formData.subject_name.trim()
            );


            data.append(
                "status",
                formData.status
            );


            data.append(
                "iconimage",
                formData.iconimage
            );


            /*
            |--------------------------------------------------------------------------
            | Debug
            |--------------------------------------------------------------------------
            */

            console.log(
                "ADD SUBJECT FORM DATA:",
                {
                    subject_name:
                        formData.subject_name,

                    status:
                        formData.status,

                    iconimage:
                        formData.iconimage
                            ? formData.iconimage.name
                            : null
                }
            );


            /*
            |--------------------------------------------------------------------------
            | API Request
            |--------------------------------------------------------------------------
            */

            const response = await fetch(

                `${API_BASE_URL}/admin/subjects`,

                {

                    method: "POST",

                    headers: {

                        "Accept":
                            "application/json"

                    },

                    /*
                    |--------------------------------------------------------------------------
                    | DO NOT ADD Content-Type HERE
                    |
                    | Browser automatically sets:
                    | multipart/form-data; boundary=...
                    |--------------------------------------------------------------------------
                    */

                    credentials: "include",

                    body: data

                }

            );


            /*
            |--------------------------------------------------------------------------
            | API Response
            |--------------------------------------------------------------------------
            */

            const result =
                await response.json();


            console.log(
                "ADD SUBJECT RESPONSE:",
                result
            );


            /*
            |--------------------------------------------------------------------------
            | Success
            |--------------------------------------------------------------------------
            */

            if (
                response.ok &&
                result.success
            ) {

                await Swal.fire({

                    icon: "success",

                    title: "Success!",

                    text:
                        result.message ||
                        "Subject added successfully.",

                    timer: 1500,

                    showConfirmButton: false

                });


                navigate("/subjects");


            } else {

                /*
                |--------------------------------------------------------------------------
                | Validation Errors
                |--------------------------------------------------------------------------
                */

                let errorMessage =
                    result.message ||
                    "Something went wrong.";


                if (result.errors) {

                    const validationMessages =
                        Object.values(
                            result.errors
                        )
                            .flat()
                            .join("\n");


                    if (validationMessages) {

                        errorMessage =
                            validationMessages;

                    }

                }


                Swal.fire({

                    icon: "error",

                    title: "Unable to Add Subject",

                    text:
                        errorMessage

                });

            }


        } catch (error) {

            console.error(
                "Add Subject Error:",
                error
            );


            Swal.fire({

                icon: "error",

                title: "Server Error",

                text:
                    "Unable to connect with server."

            });

        } finally {

            setSaving(false);

        }

    };


    /*
    |--------------------------------------------------------------------------
    | UI
    |--------------------------------------------------------------------------
    */

    return (

        <div
            className="container-fluid py-4"
            style={{
                maxHeight: "500px",
                overflowX: "auto",
                overflowY: "auto"
            }}
        >


            {/* ================================================================
                PAGE HEADER
            ================================================================= */}

            <div
                className="
                    d-flex
                    justify-content-between
                    align-items-center
                    mb-4
                "
            >

                <h3 className="mb-0">

                    Add Subject

                </h3>


                <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() =>
                        navigate("/subjects")
                    }
                    disabled={saving}
                >

                    Back

                </button>

            </div>


            {/* ================================================================
                FORM CARD
            ================================================================= */}

            <div className="card shadow-sm">

                <div className="card-body">

                    <form onSubmit={handleSubmit}>


                        {/* ====================================================
                            SUBJECT NAME
                        ===================================================== */}

                        <div className="mb-3">

                            <label className="form-label">

                                Subject Name

                                <span className="text-danger">
                                    *
                                </span>

                            </label>


                            <input
                                type="text"
                                name="subject_name"
                                className="form-control"
                                placeholder="Enter subject name"
                                value={
                                    formData.subject_name
                                }
                                onChange={
                                    handleChange
                                }
                                maxLength="100"
                                disabled={saving}
                            />

                        </div>


                        {/* ====================================================
                            ICON IMAGE
                        ===================================================== */}

                        <div className="mb-3">

                            <label className="form-label">

                                Subject Icon

                                <span className="text-danger">
                                    *
                                </span>

                            </label>


                            <input
                                type="file"
                                name="iconimage"
                                className="form-control"
                                accept=".jpg,.jpeg,.png,.webp,.gif"
                                onChange={
                                    handleImageChange
                                }
                                disabled={saving}
                            />


                            <small className="text-muted">

                                JPG, JPEG, PNG, WEBP or GIF.
                                Maximum 2 MB.

                            </small>

                        </div>


                        {/* ====================================================
                            IMAGE PREVIEW
                        ===================================================== */}

                        {imagePreview && (

                            <div className="mb-3">

                                <label className="form-label">

                                    Icon Preview

                                </label>


                                <div>

                                    <img
                                        src={
                                            imagePreview
                                        }
                                        alt="Subject Icon Preview"
                                        style={{
                                            width: "100px",
                                            height: "100px",
                                            objectFit: "cover",
                                            borderRadius: "10px",
                                            border:
                                                "1px solid #ddd",
                                            padding: "4px",
                                            backgroundColor:
                                                "#fff"
                                        }}
                                    />

                                </div>

                            </div>

                        )}


                        {/* ====================================================
                            STATUS
                        ===================================================== */}

                        <div className="mb-3">

                            <label className="form-label">

                                Status

                            </label>


                            <select
                                name="status"
                                className="form-select"
                                value={
                                    formData.status
                                }
                                onChange={
                                    handleChange
                                }
                                disabled={saving}
                            >

                                <option value="Enable">

                                    Enable

                                </option>


                                <option value="Disable">

                                    Disable

                                </option>

                            </select>

                        </div>


                        {/* ====================================================
                            BUTTONS
                        ===================================================== */}

                        <div className="d-flex gap-2">

                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={saving}
                            >

                                {saving
                                    ? "Saving..."
                                    : "Save Subject"}

                            </button>


                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={() =>
                                    navigate("/subjects")
                                }
                                disabled={saving}
                            >

                                Cancel

                            </button>

                        </div>


                    </form>

                </div>

            </div>

        </div>

    );

}


export default AddSubject;